/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import { defineStore } from 'pinia';
import { CampaignAndTasksApiService } from '@/services/api';
import CampaignTaskModel from '@/model/campaign-and-tasks/task.model';

const useNodeUpdateDetailsStore = defineStore('nodeUpdateDetails', {
  state: () => ({
    campaignTaskList: [],
    totalTasks: 0,
  }),

  actions: {
    async getCampaignTasks(data) {
      if (!data || !data.id) {
        return;
      }

      const params = {
        limit: data.itemsPerPage || 10,
        page: data.page || 1,
        filterBy: {
          isFinished: data.isFinished,
          isFailed: data.isFailed,
          inProgress: data.inProgress,
        },
      };

      if (data.search !== '') {
        params.filterBy.device = data.search;
      }

      const tasks = await CampaignAndTasksApiService.getCampaignTasks({ params }, data.id);

      this.$patch({
        campaignTaskList: tasks.data.map((task) => new CampaignTaskModel({ ...task })),
        totalTasks: tasks.count,
      });
    },

    async markTaskAsFinish(taskId) {
      await CampaignAndTasksApiService.markTaskAsFinish(taskId);
    },

    /**
     * @description Handling mqtt messages coming from backend strat_bom service regarding
     * task status updates for node update campaigns.
     */
    updateTaskStatus(payload) {
      const campaignType = payload.params?.campaignType;
      if (campaignType !== 'node_update') {
        return;
      }
      const taskIncomingData = payload.params;
      const task = this.campaignTaskList.find((t) => t.id === taskIncomingData.id);
      if (!task) {
        return;
      }

      task.progress = taskIncomingData.progress;
      task.status = CampaignTaskModel.formatStatus(taskIncomingData.status);
      task.started = CampaignTaskModel.formatStarted(taskIncomingData.timeOfTaskStart);
      task.errorFeedback = taskIncomingData.errorFeedback;
      task.finished = CampaignTaskModel.formatFinished(
        taskIncomingData.status,
        taskIncomingData.taskFinishedTimestamp,
        taskIncomingData.errorFeedback,
      );
    },

    async getCampaignOperationName(campaignId) {
      const content = await CampaignAndTasksApiService.getCampaignInformation(campaignId);
      return content.operation_name;
    },
  },
});

export default useNodeUpdateDetailsStore;
